/**
 * `/auth/mfa-challenge` — the TOTP login challenge (mfa_two_factor).
 *
 * ROOT-LEVEL route (beside `auth.login.tsx` + `invite.accept.tsx`, OUTSIDE
 * `$tenantSlug`) so it mounts WITHOUT the AuthGuard in `$tenantSlug.tsx` —
 * at this point the browser only holds the short-lived `mfa-pending` cookie,
 * which grants nothing but `/auth/mfa/verify-login`.
 *
 * Flow (per docs/plans/mfa_two_factor.md):
 *   1. After a password login that returned `{requires_mfa:true}`, login.tsx
 *      navigates here (carrying `?from=`).
 *   2. Enter the 6-digit authenticator code (or toggle to a recovery code) →
 *      POST /api/shell/auth/mfa/verify-login {code} | {recovery_code}.
 *   3. On success the backend mints the real (pre-tenant or tenant-bound)
 *      session and returns the SAME `{requires_tenant_select}` shape as
 *      /auth/login — so we reuse login's post-success routing exactly:
 *        requires_tenant_select: true  -> /auth/tenant-select
 *        requires_tenant_select: false -> safeReturnPath(from)
 *   4. Wrong code -> role=alert error, focus-on-fail-only, NO session.
 *
 * A11y (a11y contract):
 *   - The code input is the sole field and autofocuses on load.
 *   - "Use a recovery code instead" is a real <button> with aria-pressed; the
 *     mode change is announced via a polite live region.
 *   - Errors render in a role=alert that receives focus only on failure.
 */

import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label } from "@ken/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";

import { OtpCodeInput } from "../components/OtpCodeInput";

interface MfaChallengeSearch {
  from?: string;
}

export const Route = createFileRoute("/auth/mfa-challenge")({
  component: MfaChallengeRoute,
  validateSearch: (search: Record<string, unknown>): MfaChallengeSearch =>
    // exactOptionalPropertyTypes: only set `from` when it is actually a
    // same-origin-ish string; never assign an explicit `undefined`.
    typeof search["from"] === "string" ? { from: search["from"] } : {},
});

interface VerifyLoginResponse {
  requires_tenant_select: boolean;
}

// Same open-redirect guard as auth.login.tsx — only same-origin app paths.
function safeReturnPath(from: string | undefined): string {
  if (!from) return "/";
  if (!from.startsWith("/")) return "/";
  if (from.startsWith("//")) return "/";
  if (from.startsWith("/auth/")) return "/";
  return from;
}

function MfaChallengeRoute(): ReactElement {
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [mode, setMode] = useState<"code" | "recovery">("code");
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announce, setAnnounce] = useState("");

  const errorRef = useRef<HTMLDivElement | null>(null);
  const recoveryRef = useRef<HTMLInputElement | null>(null);

  // Focus the error alert on failure ONLY (a11y contract).
  useEffect(() => {
    if (error && errorRef.current) errorRef.current.focus();
  }, [error]);

  function toRecovery(): void {
    setMode("recovery");
    setError(null);
    setAnnounce("Switched to recovery code entry.");
    // Move focus to the recovery field once it renders.
    setTimeout(() => recoveryRef.current?.focus(), 0);
  }

  function toCode(): void {
    setMode("code");
    setError(null);
    setAnnounce("Switched to authenticator code entry.");
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    const body =
      mode === "code"
        ? { code }
        : { recovery_code: recovery.trim() };

    // Cheap client-side guard so an empty submit doesn't burn a request.
    if (mode === "code" && code.length !== 6) {
      setError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    if (mode === "recovery" && recovery.trim().length === 0) {
      setError("Enter one of your recovery codes.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/shell/auth/mfa/verify-login", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (res.status === 401) {
        setError(
          mode === "code"
            ? "That code didn't match. Check your authenticator app and try again."
            : "That recovery code isn't valid. Try another one.",
        );
        return;
      }
      if (!res.ok) {
        setError(`Verification failed (status ${res.status}).`);
        return;
      }

      const data = (await res.json()) as VerifyLoginResponse;
      if (data.requires_tenant_select) {
        await navigate({ to: "/auth/tenant-select" });
      } else {
        // Real session minted — reuse login's post-success routing, honouring
        // the page the user was originally headed to.
        window.location.assign(safeReturnPath(search.from));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid place-items-center min-h-screen p-4 bg-[var(--color-bg-base)]">
      <Card surface="solid" className="w-full max-w-sm" padding="lg">
        <CardHeader>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="grid place-items-center w-8 h-8 rounded-[var(--radius-md)] bg-[var(--color-brand)] text-white font-bold font-[var(--font-hand)] text-[22px]">
              K
            </div>
            <div className="font-[var(--font-hand)] text-[24px] font-semibold">ken</div>
          </div>
          <CardTitle>Two-factor verification</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-sm text-[var(--color-text-soft)]">
            {mode === "code"
              ? "Enter the 6-digit code from your authenticator app to finish signing in."
              : "Enter one of the recovery codes you saved when you turned on two-factor."}
          </p>

          {/* Polite live region — announces the mode toggle without stealing
              focus (a11y contract). */}
          <div role="status" aria-live="polite" className="sr-only">
            {announce}
          </div>

          <form
            data-testid="auth-mfa-challenge-form"
            className="flex flex-col gap-4"
            onSubmit={(e) => void onSubmit(e)}
            noValidate
          >
            {mode === "code" ? (
              <OtpCodeInput
                id="mfa-challenge-code"
                label="Authenticator code"
                hint="6 digits, no spaces."
                value={code}
                onChange={setCode}
                invalid={error !== null}
                disabled={submitting}
                autoFocus
                data-testid="auth-mfa-challenge-code-input"
              />
            ) : (
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="mfa-challenge-recovery">Recovery code</Label>
                <Input
                  id="mfa-challenge-recovery"
                  ref={recoveryRef}
                  type="text"
                  autoComplete="one-time-code"
                  spellCheck={false}
                  autoCapitalize="none"
                  required
                  value={recovery}
                  invalid={error !== null}
                  data-testid="auth-mfa-challenge-recovery-input"
                  disabled={submitting}
                  onChange={(e) => setRecovery(e.target.value)}
                  className="font-mono"
                  placeholder="xxxx-xxxx-xxxx"
                />
              </div>
            )}

            {error !== null && (
              <div
                ref={errorRef}
                tabIndex={-1}
                role="alert"
                data-testid="auth-mfa-challenge-error"
                className="text-sm text-[var(--color-accent-red)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-red)]"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              data-testid="auth-mfa-challenge-submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting ? "Verifying…" : "Verify"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              data-testid="auth-mfa-challenge-use-recovery"
              aria-pressed={mode === "recovery"}
              onClick={() => (mode === "code" ? toRecovery() : toCode())}
              className="text-xs text-[var(--color-text-soft)] underline underline-offset-2 hover:text-[var(--color-text-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-soft)] rounded-[var(--radius-sm)] px-1 py-0.5"
            >
              {mode === "code"
                ? "Use a recovery code instead"
                : "Use your authenticator code instead"}
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
